import * as React from 'react';
import styled, { useTheme } from 'styled-components';

import { BREAKPOINTS, COLORS, ComponentTextStyle } from '../styles';
import { SystemIcons } from '../icons';
import { IconButton } from '../Button';
import { getButtonStyle } from './styles';
import { Size } from '../types';
import { ComponentMStyling, ComponentResponsive } from '../styles';
import HyperLink from '../HyperLink/HyperLink';
import { StyledLink } from '../HyperLink/HyperLink';
import { hasWindow } from '../utils/utils';
import { ColorVariant } from 'src/utils/color-tokens';

/**
 * Styles for <Banner />
 */
export type StyleBannerProps = BannerProps & { type: string; linkVisited: string; linkFocused: string };

export const BannerContainerStyles = styled.div<{ $link?: string, $bottom?: boolean, $backgroundColor: string; $linkActive: string; }>`
  background: ${props => props.$backgroundColor};
  min-height: 48px;
  display: flex;
  color: ${props => COLORS.getColor('black', props.theme)};
  margin-top: ${props => (props.$bottom ? 'auto' : '0')};
  border-radius: 4px;

  button:last-child {
    line-height: 0;
    margin: 0 0 0 8px;
    ${BREAKPOINTS.MEDIUM} {
      margin: 0 0 0 16px;
    }
    ${BREAKPOINTS.LARGE} {
      margin: 0 0 0 32px;
    }
    &.small {
      margin: 0 0 0 8px;
    }
    &.medium {
      margin: 0 0 0 16px;
    }
    &.large {
      margin: 0 0 0 32px;
    }
  }

  ${StyledLink} {
    &:link {
      color: ${(props) => props.$link};
    }
    &:visited {
      color: ${(props) => props.$linkActive};
    }
    &:focus,
    &:active {
      background-color: ${(props) => props.$linkActive};
      color: ${props => COLORS.getColor('white', props.theme)};
    }
  }

  &.full-width-banner {
    margin: 8px;
  }
`;

export const BannerCenterStyles = styled.div`
  ${ComponentMStyling(ComponentTextStyle.Regular, null)}

  display: flex;
  width: 100%;
  margin: 0 16px;
  ${BREAKPOINTS.MEDIUM} {
    margin: 0 32px;
  }
  ${BREAKPOINTS.LARGE} {
    margin: 0 56px;
  }
  &.small {
    margin: 0 16px;
  }
  &.medium {
    margin: 0 32px;
  }
  &.large {
    margin: 0 64px;
  }

  > svg {
    flex-shrink: 0;
    margin: 12px 0;
  }

  .full-width-banner & {
    margin: 0 16px;

    ${BREAKPOINTS.MEDIUM} {
      margin: 0 32px;
    }
    ${BREAKPOINTS.LARGE} {
      margin: 0 64px;
    }

    &.small {
      margin: 0 16px;
    }

    &.medium {
      margin: 0 32px;
    }

    &.large {
      margin: 0 64px;
    }
  }
`;

export const BannerCloseButtonWrapperStyles = styled.div`
  margin: 0 0 0 auto;
`;

export const BannerCloseButtonContainerStyles = styled.div<{ $type?: string }>`
  display: contents;
  ${(props) => (props.$type ? getButtonStyle(props.$type, props.theme) : null)};
`;

export const BannerContentWrapperStyles = styled.div`
  display: flex;
  align-items: center;
  margin: 12px 0;
  &:not(:first-child) {
    margin-left: 8px;
  }
`;

export type BannerProps = React.HTMLAttributes<HTMLDivElement> & {
  /** Size of the banner. */
  size?: Size.Small | Size.Medium | Size.Large;
  /** Type of the banner. Different types have different color schemes. */
  type?: 'warning' | 'critical' | 'positive' | 'neutral';
  /** Link that will be shown after the children of the component. */
  link?: string;
  /** Text of the link */
  linkText?: string;
  /** If provided, then after user clicks on the link instead of following the link this method will be executed. */
  linkAction?: (ev: React.MouseEvent) => void;
  /** If set to true, then Banner will have 'margin-top' set to auto, and will render it at the bottom of the parent. */
  bottom?: boolean;
  /** For testing purposes. Will be set on top level container. */
  testId?: string;
  /** Icon to be shown instead of default icon on the left side of the Banner. */
  icon?: React.ReactNode;
  /** Callback event to be executed when user clicks on Close button. */
  onClose?: () => void;
  /** By default different banner types have different icons shown. If this flag is set, then no icon will be shown */
  noIcon?: boolean;
  /** Modifies margin of the banner for the cases when Banner stretches to full page width. */
  fullWidth?: boolean;
};

/** Banner component is used as a middle-interrupting message to the user with zero to one actions. */
const Banner: React.FunctionComponent<BannerProps> = ({
  size,
  type = 'neutral',
  children,
  bottom,
  testId,
  linkText,
  link,
  onClose,
  icon,
  noIcon,
  linkAction,
  fullWidth,
  className,
  ...rest
}) => {
  const theme = useTheme();
  const [width, setWidth] = React.useState<number>(hasWindow() ? window.innerWidth : -1);
  React.useEffect(() => {
    function handleResize() {
      setWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  let variant = 'primary';
  switch(type)
  {
    case 'warning':
      variant = 'warning';
      break;

    case 'critical':
      variant = 'critical';
      break;

    case 'positive':
      variant = 'positive';
      break;
  }
  
  let bannerParams = {
    background:     COLORS.generateToken({ componentType: 'bg-surface', defaultVariant: variant as ColorVariant }, theme),
    linkColor:      COLORS.generateToken({ componentType: 'text-link',  defaultVariant: variant as ColorVariant }, theme),
    closeIconColor: COLORS.generateToken({ componentType: 'icon',       defaultVariant: variant as ColorVariant }, theme),
    icon: SystemIcons.Tip,
    containerType: type,
    linkActive:     COLORS.generateToken({ componentType: 'text-link',  defaultVariant: variant as ColorVariant, state: 'active' }, theme),
  };

  switch (type) {
    case 'warning':
      bannerParams.icon = SystemIcons.Help;
      break;
    case 'critical':
      bannerParams.icon = SystemIcons.TechnicalWarning;
      break;
    case 'positive':
      bannerParams.icon = SystemIcons.ThumbsUp;
      break;
  }

  const cls = `${className ?? ''} ${fullWidth ? 'full-width-banner' : ''}`;

  return (
    <BannerContainerStyles
      data-testid={testId}
      $backgroundColor={bannerParams.background}
      $linkActive={bannerParams.linkActive}
      $link={bannerParams.linkColor}
      $bottom={bottom}
      className={cls}
      role="note"
      {...rest}>
      <BannerCenterStyles style={{ color: bannerParams.linkColor }} className={size}>
        {icon ? icon : noIcon ? null : <bannerParams.icon color={bannerParams.linkColor} size="24px" />}
        <BannerContentWrapperStyles>
          <ComponentResponsive size={size} width={width} color={bannerParams.linkColor}>
            {children}&nbsp;
            {link && linkText && (
              <HyperLink
                id={`${Math.floor(Math.random() * 999999999999)}_BannerLink`}
                onClick={(e) => {
                  if (linkAction) {
                    e.preventDefault();
                    linkAction(e);
                  }
                }}
                href={link}
                variant="default">
                {linkText}
              </HyperLink>
            )}
          </ComponentResponsive>
        </BannerContentWrapperStyles>
        {onClose && (
          <BannerCloseButtonWrapperStyles>
            <BannerCloseButtonContainerStyles $type={bannerParams.containerType}>
              <IconButton variant="secondary" shape="circular" useTransparentBackground action={() => onClose()}>
                <SystemIcons.Close color={bannerParams.closeIconColor} size="24px" />
              </IconButton>
            </BannerCloseButtonContainerStyles>
          </BannerCloseButtonWrapperStyles>
        )}
      </BannerCenterStyles>
    </BannerContainerStyles>
  );
};

export default Banner;
